import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useSocket } from './SocketClusterContext';
import { collectSegments, pharmacyPickupSegments, customerPickupSegments, deliverySegments } from 'constants/orderSegments';
import OrderRequest from 'components/OrderRequest';
import useRequest from 'hooks/useRequest';
import useUser from 'hooks/useUser';
import { USER_ROLES } from 'utils/constants';
import { useBasket } from './BasketContext';
import ConnectionStatusBanner from 'components/ConnectionStatusBanner';
import { getStatusMapping } from 'hooks/useActiveOrderChecker';

// TODO: change delivery/package/pickup to shopper/pharmacy/customer

/**
 * @typedef {Object} Product
 * @property {number} id - Unique identifier for the product in the order
 * @property {number} productId - Reference to the master product ID
 * @property {number} quantity - Number of items ordered
 * @property {number} price - Price per unit
 * @property {?number} productId_Dyn_Sort - Dynamic sorting field for product ID
 * @property {string} name - Product name/title
 * @property {string} packaging - Type of packaging (e.g., "ST" for standard)
 * @property {number} packageQuantity - Number of units in package
 */

/**
 * @typedef {Object} CustomerAddress
 * @property {number} id - Address ID
 * @property {string} cityName - City or street name
 * @property {string} streetName - Street name
 * @property {string} countryName - Country name
 * @property {string} postalCode - Postal code
 * @property {string} geoLatitude - Geographic latitude
 * @property {string} geoLongitude - Geographic longitude
 * @property {string} addressLable - Address label (e.g., "home")
 */

/**
 * @typedef {Object} Customer
 * @property {number} id - Unique identifier for the customer
 * @property {string} integrationId - UUID for external system integration
 * @property {string} lastName - Customer's last name
 * @property {string} firstName - Customer's first name
 * @property {string} phoneNumber - Contact phone number
 * @property {string} userPic - URL or base64 of customer's profile picture
 * @property {?CustomerAddress} address - Customer's address information
 */

/**
 * @typedef {Object} Pharmacy
 * @property {number} id - Unique identifier for the pharmacy
 * @property {?string} created - Creation date
 * @property {?number} createdBy - User ID who created the record
 * @property {string} updated - Last update date (format: "DD.MM.YYYY HH:mm")
 * @property {number} updatedBy - User ID who last updated the record
 * @property {string} integrationId - UUID for external system integration
 * @property {number} ownerId - ID of pharmacy owner
 * @property {string} name - Pharmacy name
 * @property {string} email - Contact email
 * @property {string} telefon - Contact phone number
 * @property {string} website - Pharmacy website URL
 * @property {string} publicPermitHolder - Public permit information
 * @property {string} fax - Fax number
 * @property {string} atu - Tax identification number
 * @property {string} bio - Pharmacy description
 * @property {string} note - Additional notes
 * @property {string} pharmacyAPIP - API endpoint
 * @property {string} pharmacyAPUser - API username
 * @property {string} pharmacyAPPass - API password
 * @property {string} cityName - City location
 * @property {string} streetName - Street address
 * @property {string} postalCode - Postal code
 * @property {string} geoLatitude - Geographic latitude
 * @property {string} geoLongitude - Geographic longitude
 * @property {string} ownerId_Dyn_Sort - Dynamic sorting field for owner
 */

/**
 * @typedef {Object} Shopper
 * @property {number} id - Unique identifier for the shopper
 * @property {string} integrationId - UUID for external system integration
 * @property {string} lastName - Shopper's last name
 * @property {string} firstName - Shopper's first name
 * @property {string} phoneNumber - Contact phone number
 * @property {?string} userPic - URL or base64 of shopper's profile picture
 */

/**
 * @typedef {Object} Pharmacist
 * @property {number} id - Unique identifier for the Pharmacist
 * @property {string} integrationId - UUID for external system integration
 * @property {string} lastName - Pharmacist's last name
 * @property {string} firstName - Pharmacist's first name
 * @property {string} phoneNumber - Contact phone number
 * @property {?string} userPic - URL or base64 of Pharmacist's profile picture
 */

/**
 * @typedef {Object} Prescription
 * @property {number} id - Unique identifier for the prescription
 * @property {string} created - Creation date
 * @property {number} createdBy - User ID who created
 * @property {string} updated - Update date
 * @property {number} updatedBy - User ID who updated
 * @property {string} integrationId - UUID for prescription
 * @property {string} svName - SV name
 * @property {string} svNr - Social insurance number
 * @property {string} name - Patient name
 * @property {string} address - Patient address
 * @property {string} prescriptionFeeExemption - Fee exemption status
 * @property {string} prescriptionId - Prescription ID (rez_id)
 * @property {string} eMedId - E-Med ID
 * @property {string} issuedOn - Issue date
 * @property {string} expired - Expiry date
 * @property {string} photo - URL to prescription image
 */

/**
 * @typedef {Object} Package
 * @property {number} id - Unique identifier for the package
 * @property {string} integrationId - UUID for external system integration
 * @property {number} orderId - ID of the associated order
 * @property {?boolean} wasScannedByCustomer - Indicates if scanned by customer (null if not scanned)
 * @property {?boolean} wasScannedByPharmacist - Indicates if scanned by pharmacist (null if not scanned)
 * @property {?boolean} wasScannedByShopper - Indicates if scanned by shopper (null if not scanned)
 * @property {?boolean} wasScannedByShopperTwo - Indicates if scanned by a second shopper (null if not scanned)
 */

/**
 * @typedef {Object} OrderDetails
 * @property {number} id - Unique identifier for the order
 * @property {string} created - Order creation date (format: "DD.MM.YYYY HH:mm")
 * @property {string} integrationId - UUID for order tracking
 * @property {number} customerId - Reference to customer ID
 * @property {string} customerUUID - Customer's UUID for external systems
 * @property {?number} pharmacyId - ID of assigned pharmacy (null if unassigned)
 * @property {string} pharmacyUUID - UUID of assigned pharmacy
 * @property {?number} shopperId - ID of assigned shopper (null if unassigned)
 * @property {string} shopperUUID - UUID of assigned shopper
 * @property {string} type - Order type identifier
 * @property {number} deliveryStatus - Current status of delivery (1: pending, etc.)
 * @property {?number} prescriptionId - ID of associated prescription
 * @property {Product[]} products - Array of products in the order
 * @property {Package[]} packages - Array of packages in the order
 * @property {number} totalPrice - Total order amount including all products
 * @property {Customer} customer - Customer details object
 * @property {?Pharmacy} pharmacy - Pharmacy details object
 * @property {?Shopper} shopper - Shopper details object
 * @property {?Pharmacist} pharmacist - Pharmacist details object
 * @property {?Prescription} prescription - Prescription details object
 * @property {number} customerId_Dyn_Sort - Dynamic sorting field for customer ID
 * @property {?number} pharmacyId_Dyn_Sort - Dynamic sorting field for pharmacy ID
 * @property {?number} shopperId_Dyn_Sort - Dynamic sorting field for shopper ID
 * @property {?number} prescriptionId_Dyn_Sort - Dynamic sorting field for prescription ID
 * @property {?string} orderReadyAt - Time the order got ready by pharmacist
 * @property {?string} orderDispatchedAt - Time the order got dispatched in case of delivery type 2 Mail Delivery
 * @property {?string} orderDeliveredAt - Time the order got delivered
 * @property {?string} orderConfirmedAt - Time the order got confirmed (handed to shopper)
 */

/**
 * @typedef {Object} OrderContextValue
 * @property {?OrderDetails} orderDetails - Current order being processed
 * @property {boolean} isOverlayVisible - Controls visibility of the overlay UI
 * @property {number} activeStep - Current step in the order process (0-based index)
 * @property {?Object} prescriptionInfo - Stored prescription information
 * @property {function(): void} resetOrderContext - Clears all order context state and localStorage
 * @property {function(number=): void} showOverlay - Displays overlay with optional initial step
 * @property {function(): void} hideOverlay - Hides the current overlay
 * @property {function(): void} proceedToNextStep - Advances to next step in order process
 * @property {function(): void} resetSteps - Resets step counter to 0
 * @property {function(OrderDetails, number=): void} startMatchingProcess - Initiates order matching with optional starting step
 * @property {function(): void} stopMatchingProcess - Terminates current matching process
 * @property {function(number=): ?string} getCurrentPage - Gets URL for current step based on user role
 * @property {function(React.ReactNode): void} showOverlayContent - Shows custom content in overlay
 * @property {function(): void} hideOverlayContent - Removes custom content from overlay
 * @property {function(): Promise<void>} refetchOrder - Refreshes order details from server
 * @property {function(Object): void} savePrescriptionInfo - Saves prescription information to context
 * @property {function(): void} clearPrescriptionInfo - Clears stored prescription information
 */

// Context initialization
const OrderContext = createContext(/** @type {React.Context<OrderContextValue>} */(null));
const LOCAL_STORAGE_KEY = 'orderContext';

export const OrderProvider = ({ children }) => {
    // State variables
    const [orderDetails, setOrderDetails] = useState(null);
    const [isOverlayVisible, setIsOverlayVisible] = useState(false);
    const [overlayContent, setOverlayContent] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [prescriptionInfo, setPrescriptionInfo] = useState(null);
    const [prescriptionId, setPrescriptionId] = useState(null);
    const [prescriptionProductIds, setPrescriptionProductIds] = useState([]);
    const [invoiceData, setInvoiceData] = useState(null);
    const [pharmacyInvoiceData, setPharmacyInvoiceData] = useState(null);
    const [shopperInvoiceData, setShopperInvoiceData] = useState(null);
    const { getUser } = useUser();
    const user = getUser();

    // All hooks must be called unconditionally (React rules of hooks)
    const { sendRequest } = useRequest();
    const { sendMessage, onRefetchData, isConnected } = useSocket();
    const { removeFromBasket, forceRefreshBasket } = useBasket();

    // If there's no user, return the provider with no-op functions
    if (!user) {
        return (
            <OrderContext.Provider value={{
                orderDetails: null,
                isOverlayVisible: false,
                activeStep: 0,
                prescriptionInfo: null,
                prescriptionId: null,
                prescriptionProductIds: [],
                invoiceData: null,
                pharmacyInvoiceData: null,
                shopperInvoiceData: null,
                resetOrderContext: () => { },
                showOverlay: () => { },
                hideOverlay: () => { },
                proceedToNextStep: () => { },
                resetSteps: () => { },
                startMatchingProcess: () => { },
                stopMatchingProcess: () => { },
                getCurrentPage: () => null,
                showOverlayContent: () => { },
                hideOverlayContent: () => { },
                refetchOrder: () => { },
                savePrescriptionInfo: () => { },
                clearPrescriptionInfo: () => { },
                setPrescriptionId: () => { },
                addPrescriptionProductId: () => { },
                fetchInvoiceData: () => { },
                fetchPharmacyInvoiceData: () => { },
                fetchShopperInvoiceData: () => { },
            }}>
                {children}
            </OrderContext.Provider>
        );
    }

    // Load state from localStorage on mount
    useEffect(() => {
        console.log("MOUNTED: useEffect executed");
        const savedState = localStorage.getItem(LOCAL_STORAGE_KEY);
        console.log("Saved State:", savedState);
        if (savedState) {
            const parsedState = JSON.parse(savedState);

            // Discard stale orders (>2 hours old) or terminal orders (done/cancelled/error)
            const TWO_HOURS = 2 * 60 * 60 * 1000;
            const isStale = parsedState.savedAt && (Date.now() - parsedState.savedAt > TWO_HOURS);
            const isTerminal = parsedState.orderDetails?.deliveryStatus >= 15;
            if (isStale || isTerminal) {
                console.log("Discarding stale/terminal order from localStorage", { isStale, isTerminal });
                localStorage.removeItem(LOCAL_STORAGE_KEY);
                return;
            }

            setActiveStep(parsedState.activeStep || 0);
            setOrderDetails(parsedState.orderDetails || null);
            setPrescriptionInfo(parsedState.prescriptionInfo || null);
            setPrescriptionId(parsedState.prescriptionId || null);
            setPrescriptionProductIds(parsedState.prescriptionProductIds || []);
            setInvoiceData(parsedState.invoiceData || null);
            setPharmacyInvoiceData(parsedState.pharmacyInvoiceData || null);
            setShopperInvoiceData(parsedState.shopperInvoiceData || null);
        }
    }, []);

    // Save state to localStorage whenever it changes
    useEffect(() => {
        const stateToSave = {
            orderDetails,
            activeStep,
            prescriptionInfo,
            prescriptionId,
            prescriptionProductIds,
            invoiceData,
            pharmacyInvoiceData,
            shopperInvoiceData,
            savedAt: Date.now(),
        };
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(stateToSave));
    }, [orderDetails, activeStep, prescriptionInfo, prescriptionId, prescriptionProductIds, invoiceData, pharmacyInvoiceData, shopperInvoiceData]);

    const resetOrderContext = useCallback(() => {
        setActiveStep(0);
        setOrderDetails(null);
        setIsOverlayVisible(false);
        setOverlayContent(null);
        setPrescriptionInfo(null);
        setPrescriptionId(null);
        setPrescriptionProductIds([]);
        setInvoiceData(null);
        setPharmacyInvoiceData(null);
        setShopperInvoiceData(null);
        localStorage.removeItem(LOCAL_STORAGE_KEY);
    }, []);

    const refetchOrder = useCallback(async () => {
        if (!orderDetails?.integrationId) return;

        try {
            const response = await sendRequest({
                method: 'POST',
                url: `/module/order/getOrder`,
                data: { orderUUID: orderDetails.integrationId },
            }, 3, 2000);

            const order = response.data;
            // If order reached a terminal state, clean up instead of restoring stale data
            if (order?.deliveryStatus >= 15) {
                resetOrderContext();
                return;
            }
            setOrderDetails(order);
            // Sync activeStep with server using role/type-aware mapping
            if (typeof order.deliveryStatus === 'number' && user?.userRole) {
                const mapping = getStatusMapping(user.userRole, order.type || 0);
                const mappedStep = mapping[String(order.deliveryStatus)];
                if (mappedStep !== undefined) {
                    setActiveStep(mappedStep);
                }
            }
        } catch (error) {
            console.error("refetchOrder failed:", error);
        }
    }, [orderDetails?.integrationId, sendRequest, resetOrderContext]);

    useEffect(() => {
        if (orderDetails) {
            refetchOrder();
        }
    }, [activeStep]); // eslint-disable-line

    // Callback functions
    const showOverlayContent = useCallback((content) => {
        setOverlayContent(content);
        setIsOverlayVisible(true);
    }, []);

    const hideOverlayContent = useCallback(() => {
        setOverlayContent(null);
        setIsOverlayVisible(false);
    }, []);

    const showOverlay = useCallback((initialStep = 0) => {
        setActiveStep(initialStep);
        setIsOverlayVisible(true);
    }, []);

    const hideOverlay = useCallback(() => {
        setIsOverlayVisible(false);
        setOverlayContent(null);
    }, []);

    const resetSteps = useCallback(() => {
        setActiveStep(0);
    }, []);

    const startMatchingProcess = useCallback(
        (order, activeStep = 0) => {
            console.log('startMatchingProcess called with order:', order);
            setOrderDetails(order);
            setActiveStep(activeStep);
            setIsOverlayVisible(false);

            // Immediately save to localStorage to avoid race conditions
            const stateToSave = {
                orderDetails: order,
                activeStep: activeStep,
                prescriptionInfo,
                prescriptionId,
                prescriptionProductIds,
                invoiceData,
                pharmacyInvoiceData,
                shopperInvoiceData,
                savedAt: Date.now(),
            };
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(stateToSave));
            console.log('Saved to localStorage:', stateToSave);
        },
        [prescriptionInfo, prescriptionId, prescriptionProductIds, invoiceData, pharmacyInvoiceData, shopperInvoiceData]
    );

    const proceedToNextStep = useCallback(() => {
        setActiveStep((prevStep) => prevStep + 1);
    }, []);

    const stopMatchingProcess = useCallback(() => {
        resetSteps();
        hideOverlay();
        setActiveStep(0);
        setOrderDetails(null);
    }, [resetSteps, hideOverlay]);

    const getCurrentPage = useCallback((activeStep = 0) => {
        if (user.userRole === USER_ROLES.CONSUMER_ROLE) {
            return `/matchmaking/customer/${activeStep}`;
        } else if (user.userRole === USER_ROLES.PHARMACY_ROLE) {
            return `/matchmaking/pharmacy/${activeStep}`;
        } else if (user.userRole === USER_ROLES.SHOPPER_ROLE) {
            return `/matchmaking/shopper/${activeStep}`;
        }
        return null; // Default fallback if no match
    }, [user.userRole]);

    const savePrescriptionInfo = useCallback((prescriptionData, imageUrl = null) => {
        return new Promise((resolve) => {
            if (prescriptionData && prescriptionData.scan_results) {
                const info = {
                    name: prescriptionData.scan_results.name,
                    svnr: prescriptionData.scan_results.svnr,
                    rezId: prescriptionData.scan_results.rez_id,
                    eMedId: prescriptionData.scan_results.e_med_id,
                    issuedDate: prescriptionData.scan_results.issued_date,
                    expiredDate: prescriptionData.scan_results.expired_date,
                    prescriptionFeeExemption: prescriptionData.scan_results.prescription_fee_exemption,
                    address: prescriptionData.scan_results.address_1,
                    prescriptionImageUrl: imageUrl || null,
                    photo: imageUrl || null // Also save as 'photo' for consistency with orderDetails.prescription
                };
                setPrescriptionInfo(info);
                // Give React time to update state and trigger localStorage save
                setTimeout(() => resolve(info), 100);
            } else {
                resolve(null);
            }
        });
    }, []);

    const addPrescriptionProductId = useCallback((productId) => {
        setPrescriptionProductIds(prev => {
            if (!prev.includes(productId)) {
                return [...prev, productId];
            }
            return prev;
        });
    }, []);

    const clearPrescriptionInfo = useCallback(async () => {
        // Remove all products that were added from the prescription
        if (prescriptionProductIds.length > 0) {
            try {
                // Remove each product from basket
                for (const productId of prescriptionProductIds) {
                    try {
                        await removeFromBasket(productId);
                    } catch (error) {
                        console.error(`Failed to remove prescription product ${productId}:`, error);
                    }
                }
                // Refresh basket after removing all prescription products
                await forceRefreshBasket();
            } catch (error) {
                console.error('Error removing prescription products:', error);
            }
        }

        setPrescriptionInfo(null);
        setPrescriptionId(null);
        setPrescriptionProductIds([]);
    }, [prescriptionProductIds, removeFromBasket, forceRefreshBasket]);

    const fetchInvoiceData = useCallback(async (orderId) => {
        if (invoiceData) {
            return invoiceData;
        }
        
        try {
            const response = await sendRequest({
                method: 'POST',
                url: '/module/order/getCustomerOrderInvoice',
                data: { orderId },
            });
            if (response.status === 'success') {
                setInvoiceData(response.data);
                return response.data;
            }
        } catch (err) {
            console.error('Failed to fetch invoice data:', err);
            throw err;
        }
    }, [invoiceData, sendRequest]);

    const fetchPharmacyInvoiceData = useCallback(async (orderId) => {
        if (pharmacyInvoiceData) {
            return pharmacyInvoiceData;
        }
        
        try {
            const response = await sendRequest({
                method: 'POST',
                url: '/module/order/getOrderInvoice',
                data: { orderId },
            });
            if (response.status === 'success') {
                setPharmacyInvoiceData(response.data);
                return response.data;
            }
        } catch (err) {
            console.error('Failed to fetch pharmacy invoice data:', err);
            throw err;
        }
    }, [pharmacyInvoiceData, sendRequest]);

    const fetchShopperInvoiceData = useCallback(async (orderId) => {
        if (shopperInvoiceData) {
            return shopperInvoiceData;
        }
        
        try {
            const response = await sendRequest({
                method: 'POST',
                url: '/module/order/getShopperInvoice',
                data: { orderId },
            });
            if (response.status === 'success') {
                setShopperInvoiceData(response.data);
                return response.data;
            }
        } catch (err) {
            console.error('Failed to fetch shopper invoice data:', err);
            throw err;
        }
    }, [shopperInvoiceData, sendRequest]);

    useEffect(() => {
        const cleanupRefetchData = onRefetchData((data, socket) => {
            if (data) {
                console.log("============= SOCKET DATA =============")
                console.log({ data });

                // Send acknowledgment if this notification has an ID
                if (data.notificationId && socket) {
                    socket.emit('notificationAck', {
                        notificationId: data.notificationId
                    });
                    console.log('Sent acknowledgment for notification:', data.notificationId);
                }

                // Log if this is a retry
                if (data.isRetry) {
                    console.log(`Received retry notification (attempt ${data.retryAttempt}):`, data.notificationId);
                }

                // Only process if notificationType exists (ignore chat messages and other non-order notifications)
                if (data.notificationType) {
                    if (data.notificationType.includes('ORDER_PROGRESS_')) {
                        setActiveStep(Number(data.notificationType.split("_")[2]));
                    }
                    if (data.notificationType.includes('ORDER_REQUEST')) {
                        // Check if user is a shopper and if they're available
                        if (user.userRole === USER_ROLES.SHOPPER_ROLE) {
                            const isAvailable = localStorage.getItem('shopperAvailability') === 'true';
                            if (!isAvailable) {
                                return; // Don't show the request if shopper is unavailable
                            }
                        }
                        const { uuid } = data.notificationContent;
                        showOverlayContent(
                            <OrderRequest uuid={uuid} />
                        );
                    }
                    if (data.notificationType.includes('ORDER_CANCELED')) {
                        stopMatchingProcess();
                        resetOrderContext();
                        window.location = '/';
                    }
                }
            }
        });

        return () => {
            if (cleanupRefetchData) {
                cleanupRefetchData();
            }
        };
    }, [onRefetchData, showOverlayContent]);

    // --- Socket disconnect resilience ---
    const [reconnectCountdown, setReconnectCountdown] = useState(15);
    const refetchOrderRef = useRef(refetchOrder);
    refetchOrderRef.current = refetchOrder;

    // Polling fallback: refetch every 15s when socket is down during active order
    const hasActiveOrder = !!orderDetails?.integrationId;
    useEffect(() => {
        if (isConnected || !hasActiveOrder) {
            setReconnectCountdown(15);
            return;
        }

        const interval = setInterval(() => {
            setReconnectCountdown(prev => {
                if (prev <= 1) {
                    refetchOrderRef.current();
                    return 15;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [isConnected, hasActiveOrder]);

    // Refetch immediately when socket reconnects
    const prevConnectedRef = useRef(isConnected);
    useEffect(() => {
        if (isConnected && !prevConnectedRef.current && hasActiveOrder) {
            refetchOrderRef.current();
        }
        prevConnectedRef.current = isConnected;
    }, [isConnected, hasActiveOrder]);

    // Refetch when user returns to tab/app
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && orderDetails?.integrationId) {
                refetchOrderRef.current();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [orderDetails?.integrationId]);

    // Function to get overlay content based on the current state
    const getOverlayContent = () => {
        if (overlayContent) {
            return overlayContent;
        }
        // Do not return ProgressOverlay here
        return null;
    };

    // Provide state and functions to all components in the tree
    return (
        <OrderContext.Provider
            value={{
                orderDetails,
                isOverlayVisible,
                activeStep,
                prescriptionInfo,
                prescriptionId,
                prescriptionProductIds,
                invoiceData,
                pharmacyInvoiceData,
                shopperInvoiceData,
                resetOrderContext,
                showOverlay,
                hideOverlay,
                proceedToNextStep,
                resetSteps,
                startMatchingProcess,
                stopMatchingProcess,
                getCurrentPage,
                showOverlayContent,
                hideOverlayContent,
                refetchOrder,
                savePrescriptionInfo,
                clearPrescriptionInfo,
                setPrescriptionId,
                addPrescriptionProductId,
                fetchInvoiceData,
                fetchPharmacyInvoiceData,
                fetchShopperInvoiceData,
            }}
        >
            <ConnectionStatusBanner
                isConnected={isConnected}
                countdown={reconnectCountdown}
                hasActiveOrder={hasActiveOrder}
            />
            {children}
            {isOverlayVisible && overlayContent && (
                <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50">
                    <div className="relative w-full h-full flex items-center justify-center">
                        {overlayContent}
                    </div>
                </div>
            )}
        </OrderContext.Provider>
    );
};

/**
 * Custom hook to use the OrderContext
 * @returns {OrderContextValue}
 */
export const useOrder = () => {
    const context = useContext(OrderContext);
    if (!context) {
        throw new Error('useOrder must be used within an OrderProvider');
    }
    return context;
};
